// pages/TranslationSetup.jsx with clear upload states
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

// Services
import translationService from '../services/translationService';
import documentService from '../services/documentService';
import useApiClient from '../services/api';

// Components
import Spinner from '../components/Spinner';
import DocumentViewer from '../components/DocumentViewer';
import LanguageSelect from '../components/LanguageSelect';

const TranslationSetup = () => {
  const { t } = useTranslation();
  const { translationId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('projectId');
  const isDocumentUpload = searchParams.get('mode') === 'upload';
  const apiClient = useApiClient();

  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [documentHeight, setDocumentHeight] = useState(600);
  const [documentFile, setDocumentFile] = useState(null);
  const [documentFiles, setDocumentFiles] = useState([]);
  const [uploadedDocument, setUploadedDocument] = useState(null);
  const [uploadState, setUploadState] = useState('none'); // 'none', 'selected', 'uploading', 'uploaded'
  const [uploadProgress, setUploadProgress] = useState(0);
  const [project, setProject] = useState(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState(null);
  const [translation, setTranslation] = useState({
    originalDocument: null,
    sourceLanguage: '',
    targetLanguage: '',
    documentType: '',
    documentHolder: '',
    status: ''
  });

  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [additionalContextError, setAdditionalContextError] = useState(false);
  
  // Form state for translation context
  const [translationContext, setTranslationContext] = useState({
    translationMode: 'standard',
    additionalContext: '',
    templateId: '',
    sourceLanguage: 'en',
    targetLanguage: 'fr',
  });

  // Editor preference - Always use Google Docs
  const preferredEditor = 'googledocs';

  // Fetch translation data if translationId is provided
  useEffect(() => {
    const fetchTranslation = async () => {
      if (!translationId) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        
        // First, try the traditional flow to get translation by ID
        try {
          const data = await translationService(apiClient).getTranslationById(translationId);
          setTranslation(data);
          setUploadedDocument({ documentId: data.documentId || translationId });
          setTranslationContext(prev => ({
            ...prev,
            sourceLanguage: data.sourceLanguage || prev.sourceLanguage,
            targetLanguage: data.targetLanguage || prev.targetLanguage,
            additionalContext: data.additionalContext || prev.additionalContext,
          }));
          setUploadState('uploaded'); // Mark as uploaded if we have existing data
        } catch (error) {
          console.log('Not a traditional translation ID, trying as document ID');
          
          // If not found, assume it's a document ID from new flow
          const statusData = await documentService(apiClient).checkTranslationStatus(translationId);
          
          // Set the preview URL for the original document
          const previewUrl = documentService(apiClient).getOriginalDocumentPreviewUrl(translationId);
          setUploadedDocument({ documentId: translationId });
          setTranslationContext(prev => ({
            ...prev,
            sourceLanguage: statusData.translation?.sourceLanguage || statusData.sourceLanguage || prev.sourceLanguage,
            targetLanguage: statusData.translation?.targetLanguage || statusData.targetLanguage || prev.targetLanguage,
            additionalContext: statusData.translation?.additionalContext || statusData.additionalContext || prev.additionalContext,
          }));
          // Update translation state with the document ID and preview URL
          setTranslation(prev => ({
            ...prev,
            originalDocument: previewUrl,
            documentId: translationId
          }));
          
          setUploadState('uploaded'); // Mark as uploaded since document exists
        }
        
        // Adjust document height based on window size
        adjustDocumentHeight();
      } catch (error) {
        console.error('Error fetching translation:', error);
        toast.error(t('dashboard.messages.loadDataFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchTranslation();
    
    // Listen for window resize to adjust document height
    window.addEventListener('resize', adjustDocumentHeight);
    
    // Clean up event listener
    return () => {
      window.removeEventListener('resize', adjustDocumentHeight);
    };
  }, [translationId, apiClient]);

  // Load project data if projectId is provided
  useEffect(() => {
    const loadProject = async () => {
      if (projectId) {
        try {
          const response = await apiClient.get(`/projects/${projectId}`);
          setProject(response.data.project);
        } catch (error) {
          console.error('Error loading project:', error);
          toast.error(t('dashboard.messages.loadDataFailed'));
        }
      }
    };

    loadProject();
  }, [projectId, apiClient]);
  
  // Function to adjust document height based on window size
  const adjustDocumentHeight = () => {
    const windowHeight = window.innerHeight;
    
    const newHeight = Math.max(
      Math.min(Math.floor(windowHeight * 0.6), 800),
      400
    );
    
    setDocumentHeight(newHeight);
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTranslationContext(prev => ({ ...prev, [name]: value }));
    if (name === 'additionalContext' && value.trim()) {
      setAdditionalContextError(false);
    }
  };

  // Handle file selection - supports single or multiple files
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);

    if (isDocumentUpload) {
      // Multiple file upload mode
      if (files.length > 10) {
        toast.error(t('dashboard.messages.loadDataFailed'));
        return;
      }
      setDocumentFiles(files);
      setUploadState('selected');
      setUploadProgress(0);
    } else {
      // Single file translation mode
      const file = files[0];
      if (file) {
        setDocumentFile(file);
        setUploadState('selected');
        setUploadProgress(0);

        // Generate preview URL for the selected file
        generateFilePreview(file);

        // Clear any previous upload state
        setUploadedDocument(null);
        setTranslation(prev => ({
          ...prev,
          originalDocument: null,
          documentType: '',
          documentHolder: '',
          documentId: null
        }));
      }
    }
  };

  // Generate preview URL for selected file
  const generateFilePreview = (file) => {
    // Clean up previous preview URL if it exists
    if (filePreviewUrl) {
      URL.revokeObjectURL(filePreviewUrl);
    }

    // Create new preview URL based on file type
    const fileType = file.type;

    if (fileType === 'application/pdf' || fileType.startsWith('image/')) {
      // For PDFs and images, create object URL directly
      const objectUrl = URL.createObjectURL(file);
      setFilePreviewUrl(objectUrl);
    } else {
      // For other file types (Word docs, etc.), we can't preview directly
      setFilePreviewUrl(null);
      toast.info(t('dashboard.messages.loadDataFailed'));
    }
  };

  // Clean up preview URL when component unmounts
  useEffect(() => {
    return () => {
      if (filePreviewUrl) {
        URL.revokeObjectURL(filePreviewUrl);
      }
    };
  }, [filePreviewUrl]);

  // Trigger file input click
  const handleBrowseClick = () => {
    fileInputRef.current.click();
  };

  // Handle drag events
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);

    if (isDocumentUpload) {
      // Multiple file upload mode
      if (files.length > 10) {
        toast.error(t('dashboard.messages.loadDataFailed'));
        return;
      }
      setDocumentFiles(files);
      setUploadState('selected');
      setUploadProgress(0);
    } else {
      // Single file translation mode
      const file = files[0];
      if (file) {
        setDocumentFile(file);
        setUploadState('selected');
        setUploadProgress(0);

        // Generate preview URL for the selected file
        generateFilePreview(file);

        // Clear any previous upload state
        setUploadedDocument(null);
        setTranslation(prev => ({
          ...prev,
          originalDocument: null,
          documentType: '',
          documentHolder: '',
          documentId: null
        }));
      }
    }
  };

  // Upload document(s) - handles both single and multiple files
  const handleUploadDocument = async () => {
    const filesToUpload = isDocumentUpload ? documentFiles : [documentFile];

    if (filesToUpload.length === 0) {
      toast.error(t('googleDocs.messages.selectFileFirst'));
      return;
    }

    try {
      setUploadState('uploading');
      setProcessing(true);
      setUploadProgress(0);
      
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev < 90) {
            return prev + 10;
          }
          return prev;
        });
      }, 300);
      
      let result;
      
      if (isDocumentUpload) {
        // Multiple document upload to project
        const formData = new FormData();
        filesToUpload.forEach((file) => {
          formData.append('documents', file);
        });
        
        const response = await apiClient.post(`/document/${projectId}/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        result = response.data;
      } else {
        // Single document upload for translation
        result = await documentService(apiClient).uploadDocument(documentFile, projectId);
      }
      
      // Complete the progress
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      if (isDocumentUpload) {
        // Document upload mode - redirect to project details
        toast.success(t('googleDocs.messages.uploadSuccess'));
        setTimeout(() => {
          navigate(`/project/${projectId}`);
        }, 1000);
      } else {
        // Translation mode - continue with existing flow
        setUploadedDocument(result);
        setUploadState('uploaded');

        toast.success(t('googleDocs.messages.uploadSuccess'));
        
        // Get preview URL for original document
        const previewUrl = documentService(apiClient).getOriginalDocumentPreviewUrl(result.documentId);
        
        // Update translation info with uploaded document data
        setTranslation(prev => ({
          ...prev,
          originalDocument: previewUrl,
          documentType: documentFile.type,
          documentHolder: documentFile.name,
          documentId: result.documentId
        }));
      }
      
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error(t('googleDocs.messages.uploadFailed', { error: error.message }));
      setUploadState('selected'); // Go back to selected state
      setUploadProgress(0);
    } finally {
      setProcessing(false);
    }
  };

  // Start the translation process
  const handleStartTranslation = async () => {
    try {
      if (!uploadedDocument?.documentId) {
        toast.error(t('googleDocs.messages.selectFileFirst'));
        return;
      }

      const trimmedAdditionalContext = translationContext.additionalContext.trim();
      if (!trimmedAdditionalContext) {
        setAdditionalContextError(true);
        toast.error(t('translationSetup.messages.additionalContextRequired'));
        return;
      }
      
      setProcessing(true);
      
      // Prepare translation options
      const translationOptions = {
        sourceLanguage: translationContext.sourceLanguage,
        targetLanguage: translationContext.targetLanguage,
        additionalContext: trimmedAdditionalContext,
        mode: translationContext.translationMode,
        templateId: translationContext.templateId || undefined
      };
      
      // Trigger translation
      const result = await documentService(apiClient).triggerTranslation(
        uploadedDocument.documentId, 
        translationOptions
      );
      
      toast.success(t('googleDocs.messages.translating'));

      // Store job ID
      const jobId = result.jobId;

      // Poll for translation status
      await pollTranslationStatus(uploadedDocument.documentId, jobId);
    } catch (error) {
      console.error('Error starting translation:', error);
      toast.error(t('googleDocs.messages.translateFailed', { error: error.message }));
    } finally {
      setProcessing(false);
    }
  };
  
  // Poll for translation status until it's ready
  const pollTranslationStatus = async (documentId, jobId) => {
    try {
      let isCompleted = false;
      const maxAttempts = 60; // Increased from 30 to 60 (5 minutes total)
      let attempts = 0;

      while (!isCompleted && attempts < maxAttempts) {
        attempts++;

        await new Promise(resolve => setTimeout(resolve, 5000)); // Increased from 3s to 5s
        
        const statusData = await documentService(apiClient).checkTranslationStatus(documentId);
        
        console.log('Translation status:', statusData);
        
        if (statusData.status === 'completed') {
          isCompleted = true;
          // Always use Google Docs editor
          navigate(`/translator/edit/${documentId}?mode=googledocs`);
          return;
        }

        // Check if there are any results available (hasResults in dropbox)
        if (statusData.dropbox && statusData.dropbox.hasResults) {
          console.log('Partial results available, navigating to editor');
          // Always use Google Docs editor
          navigate(`/translator/edit/${documentId}?mode=googledocs`);
          return;
        }
      }
      
      if (!isCompleted) {
        toast.info(t('common.processing'));
        navigate('/translator/dashboard');
      }
    } catch (error) {
      console.error('Error polling translation status:', error);
      toast.error(t('dashboard.messages.loadDataFailed'));
    }
  };

  if (loading) {
    return <Spinner />;
  }

  // Get document type from filename extension
  const getDocumentType = (file) => {
    if (!file) return '';
    
    let extension = '';
    if (typeof file === 'string') {
      extension = file.split('.').pop().toLowerCase();
    } else {
      extension = file.name.split('.').pop().toLowerCase();
    }
    
    switch (extension) {
      case 'pdf':
        return 'pdf';
      case 'jpg':
      case 'jpeg':
      case 'png':
        return 'image';
      case 'doc':
      case 'docx':
        return 'word';
      default:
        return '';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">
            {isDocumentUpload ? t('translationSetup.uploadDocumentsTitle') : t('translationSetup.pageTitle')}
          </h1>
          <p className="text-gray-600">
            {isDocumentUpload
              ? t('translationSetup.uploadDocumentsSubtitle')
              : t('translationSetup.pageSubtitle')
            }
          </p>
        </div>
      
        {/* Document Upload Progress */}
        {uploadState !== 'none' && (
          <div className="bg-white rounded-lg shadow-md p-4 mb-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg text-gray-900">{t('translationSetup.titles.uploadStatus')}</h4>
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                uploadState === 'selected' ? 'bg-blue-100 text-blue-800' :
                uploadState === 'uploading' ? 'bg-yellow-100 text-yellow-800' :
                'bg-green-100 text-green-800'
              }`}>
                {uploadState === 'selected' && `📄 ${t('translationSetup.status.fileSelected')}`}
                {uploadState === 'uploading' && `⏳ ${t('translationSetup.status.uploadingStatus')}`}
                {uploadState === 'uploaded' && `✅ ${t('translationSetup.status.uploadComplete')}`}
              </div>
            </div>
            
            {uploadState === 'uploading' && (
              <div className="space-y-2">
                <div className="bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <div className="text-sm text-gray-600 text-center">{uploadProgress}%</div>
              </div>
            )}
          </div>
        )}
      
        {/* Display document info if uploaded */}
        {uploadState === 'uploaded' && (translation.documentId || uploadedDocument?.documentId) && (
          <div className="bg-white rounded-lg shadow-md p-4 mb-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl text-gray-900">{t('translationSetup.titles.documentInfo')}</h2>
              <div className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-mono">
                ID: {(uploadedDocument?.documentId || translation.documentId || '').substring(0, 8)}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-500">{t('translationSetup.labels.documentType')}</label>
                <div className="text-gray-900">{getDocumentType(documentFile) || translation.documentType || 'N/A'}</div>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-500">{t('translationSetup.labels.documentName')}</label>
                <div className="text-gray-900 truncate">{documentFile?.name || translation.documentHolder || 'N/A'}</div>
              </div>
            </div>
          </div>
        )}
      
        {/* Document Uploader Section */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-4">
          <div className="mb-6">
            <h2 className="text-xl text-gray-900">{t('translationSetup.titles.uploadDocument')}</h2>
          </div>
          
          <div className="space-y-4">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              multiple={isDocumentUpload}
              className="hidden"
            />
            
            {/* File Selection State */}
            {uploadState === 'none' && (
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                  isDragging
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onClick={handleBrowseClick}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                <div className="space-y-4">
                  <div className="text-4xl">{isDragging ? '📥' : '📤'}</div>
                  <div className="space-y-2">
                    <p className={`font-medium ${isDragging ? 'text-blue-700' : 'text-gray-600'}`}>
                      {isDragging ? (
                        t('translationSetup.placeholders.dropFiles')
                      ) : (
                        <>
                          {t('translationSetup.placeholders.dragDrop')}
                        </>
                      )}
                    </p>
                    {!isDragging && (
                      <>
                        <button
                          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                          type="button"
                        >
                          {t('translationSetup.placeholders.browse')}
                        </button>
                        <p className="text-sm text-gray-500">
                          {t('translationSetup.placeholders.supportedFormats')}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          
            {/* File Selected State */}
            {uploadState === 'selected' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <div className="space-y-4">
                  {isDocumentUpload ? (
                    // Multiple files display
                    <div>
                      <div className="flex items-center space-x-2 mb-3">
                        <div className="text-2xl">📄</div>
                        <p className="font-medium text-gray-900">{documentFiles.length} {t('translationSetup.status.filesSelected')}</p>
                      </div>
                      <div className="max-h-32 overflow-y-auto space-y-2">
                        {documentFiles.map((file, index) => (
                          <div key={index} className="flex justify-between items-center bg-white p-2 rounded border">
                            <span className="text-sm text-gray-900 truncate flex-1">{file.name}</span>
                            <span className="text-xs text-gray-500 ml-2">({Math.round(file.size / 1024)} KB)</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    // Single file display
                    <div className="flex items-start space-x-3">
                      <div className="text-2xl">📄</div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{documentFile.name}</p>
                        <p className="text-sm text-gray-600">({Math.round(documentFile.size / 1024)} KB)</p>
                      </div>
                    </div>
                  )}

                  {/* Preview for single file (PDF or image) */}
                  {!isDocumentUpload && filePreviewUrl && (
                    <div className="mt-4 bg-white rounded-lg border border-gray-300 overflow-hidden">
                      <div className="bg-gray-100 px-4 py-2 border-b border-gray-300">
                        <p className="text-sm font-medium text-gray-700">{t('translationSetup.labels.preview')}</p>
                      </div>
                      <div className="p-4">
                        {documentFile?.type === 'application/pdf' ? (
                          <DocumentViewer
                            documentUrl={filePreviewUrl}
                            title={documentFile.name}
                            documentType="pdf"
                            initialHeight={400}
                            showPageControls={true}
                          />
                        ) : documentFile?.type.startsWith('image/') ? (
                          <div className="flex justify-center">
                            <img
                              src={filePreviewUrl}
                              alt="Document preview"
                              className="max-w-full max-h-96 object-contain rounded"
                            />
                          </div>
                        ) : null}
                      </div>
                    </div>
                  )}

                  <div className="flex items-start space-x-3 bg-yellow-50 border border-yellow-200 rounded-md p-3">
                    <div className="text-yellow-600">⚠️</div>
                    <div className="flex-1">
                      <p className="font-medium text-yellow-800">{t('translationSetup.status.readyToUpload')}</p>
                      <p className="text-sm text-yellow-700">
                        {isDocumentUpload
                          ? t('translationSetup.buttons.uploadDocument')
                          : t('translationSetup.buttons.uploadDocument')
                        }
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                    onClick={handleBrowseClick}
                    disabled={processing}
                  >
                    {isDocumentUpload ? t('translationSetup.buttons.changeFiles') : t('translationSetup.buttons.changeFile')}
                  </button>
                  <button
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                    onClick={handleUploadDocument}
                    disabled={processing}
                  >
                    {processing ? t('translationSetup.status.uploadingShort') : (isDocumentUpload ? t('translationSetup.buttons.uploadDocuments') : t('translationSetup.buttons.uploadDocument'))}
                  </button>
                </div>
              </div>
            )}
          
            {/* Uploading State */}
            {uploadState === 'uploading' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-8 text-center">
                <div className="space-y-4">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                  <div className="space-y-2">
                    <p className="text-lg font-medium text-gray-900">{t('translationSetup.status.uploading')}</p>
                    <p className="text-sm text-gray-600">{t('translationSetup.status.dontClose')}</p>
                  </div>
                </div>
              </div>
            )}
          
            {/* Upload Complete State */}
            {uploadState === 'uploaded' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                <div className="space-y-4">
                  <div className="text-4xl">✅</div>
                  <div className="space-y-2">
                    <p className="text-lg font-semibold text-green-800">{t('translationSetup.status.uploadSuccessful')}</p>
                    <p className="text-green-700">{t('translationSetup.status.documentReady')}</p>
                  </div>
                  {documentFile && (
                    <div className="flex items-center justify-center space-x-4 pt-4">
                      <span className="text-sm text-gray-600">{documentFile.name}</span>
                      <button 
                        className="text-sm px-3 py-1 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                        onClick={handleBrowseClick}
                      >
                        Upload Different File
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Original Document Viewer - Only show after upload and in translation mode */}
        {uploadState === 'uploaded' && !isDocumentUpload && translation.originalDocument && (
          <div className="bg-white rounded-lg shadow-md p-4 mb-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl text-gray-900">{t('translationSetup.titles.documentPreview')}</h2>
              <a 
                href={translation.originalDocument} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                View Original
              </a>
            </div>
            <DocumentViewer 
              documentUrl={translation.originalDocument} 
              title={`${translation.documentType || 'Document'} - ${translation.documentHolder || 'Preview'}`}
              documentType={getDocumentType(documentFile) || getDocumentType(translation.originalDocument)}
              initialHeight={documentHeight}
              showPageControls={true}
            />
          </div>
        )}
      
        {/* Translation Options Form - Only show after upload and in translation mode */}
        {uploadState === 'uploaded' && !isDocumentUpload && (
          <div className="bg-white rounded-lg shadow-md p-4 mb-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">{t('translationSetup.titles.translationOptions')}</h2>
              <div className="flex items-center">
                {translationContext.translationMode === 'standard' && (
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">{t('translationSetup.modes.standard')}</span>
                )}
                {translationContext.translationMode === 'interactive' && (
                  <span className="px-3 py-1 bg-cyan-100 text-cyan-800 text-sm font-medium rounded-full">{t('translationSetup.modes.interactive')}</span>
                )}
                {translationContext.translationMode === 'template' && (
                  <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">{t('translationSetup.modes.template')}</span>
                )}
              </div>
            </div>

            <form className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label htmlFor="sourceLanguage" className="block text-sm font-medium text-gray-700">
                    {t('translationSetup.labels.sourceLanguage')}
                  </label>
                  <LanguageSelect
                    id="sourceLanguage"
                    name="sourceLanguage"
                    value={translationContext.sourceLanguage}
                    onChange={handleInputChange}
                    disabled={processing}
                    grouped={true}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                  />
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="targetLanguage" className="block text-sm font-medium text-gray-700">
                    {t('translationSetup.labels.targetLanguage')}
                  </label>
                  <LanguageSelect
                    id="targetLanguage"
                    name="targetLanguage"
                    value={translationContext.targetLanguage}
                    onChange={handleInputChange}
                    disabled={processing}
                    grouped={true}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                  />
                </div>
              </div>
            
              <div className="space-y-2">
                <label htmlFor="translationMode" className="block text-sm font-medium text-gray-700">
                  {t('translationSetup.labels.translationMode')}
                </label>
                <select
                  id="translationMode"
                  name="translationMode"
                  value={translationContext.translationMode}
                  onChange={handleInputChange}
                  disabled={processing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                >
                  <option value="standard">{t('translationSetup.options.standardTranslation')}</option>
                  {/* <option value="interactive">Interactive Translation (with questions)</option>
                  <option value="template">Template-Based Translation</option> */}
                </select>
                <p className="text-sm text-gray-600">
                  {translationContext.translationMode === 'standard' &&
                    t('translationSetup.modeDescriptions.standard')}
                  {translationContext.translationMode === 'interactive' &&
                    t('translationSetup.modeDescriptions.interactive')}
                  {translationContext.translationMode === 'template' &&
                    t('translationSetup.modeDescriptions.template')}
                </p>
              </div>

              {/* Editor Info - Google Docs Only */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  📝 Editing Interface
                </label>
                <div className="text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-md p-3">
                  <div>
                    <strong className="text-blue-700">✨ Google Docs Editor</strong>
                    <ul className="mt-1 ml-4 list-disc space-y-1">
                      <li>{t('translationSetup.editorBenefits.nativeExperience')}</li>
                      <li>{t('translationSetup.editorBenefits.betterFormatting')}</li>
                      <li>{t('translationSetup.editorBenefits.collaboration')}</li>
                      <li>{t('translationSetup.editorBenefits.requiresSignIn')}</li>
                    </ul>
                  </div>
                </div>
              </div>

              {translationContext.translationMode === 'template' && (
                <div className="space-y-2">
                  <label htmlFor="templateId" className="block text-sm font-medium text-gray-700">
                    {t('translationSetup.labels.selectTemplate')}
                  </label>
                  <select
                    id="templateId"
                    name="templateId"
                    value={translationContext.templateId}
                    onChange={handleInputChange}
                    disabled={processing}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                  >
                    <option value="">{t('translationSetup.options.selectTemplate')}</option>
                    <option value="birth_certificate">{t('translationSetup.options.birthCertificate')}</option>
                    <option value="drivers_license">{t('translationSetup.options.driversLicense')}</option>
                    <option value="passport">{t('translationSetup.options.passport')}</option>
                    <option value="academic_transcript">{t('translationSetup.options.academicTranscript')}</option>
                  </select>
                </div>
              )}
            
              <div className="space-y-2">
                <label htmlFor="additionalContext" className="block text-sm font-medium text-gray-700">
                  {t('translationSetup.labels.additionalContext')} <span className="text-red-600">*</span>
                </label>
                <textarea
                  id="additionalContext"
                  name="additionalContext"
                  value={translationContext.additionalContext}
                  onChange={handleInputChange}
                  placeholder={t('translationSetup.placeholders.additionalContext')}
                  rows={4}
                  disabled={processing}
                  required
                  aria-invalid={additionalContextError}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 disabled:bg-gray-50 disabled:text-gray-500 ${
                    additionalContextError
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-200'
                      : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                  }`}
                />
                {additionalContextError && (
                  <p className="text-sm text-red-600">
                    {t('translationSetup.messages.additionalContextRequired')}
                  </p>
                )}
                <p className="text-sm text-gray-600">
                  {t('translationSetup.helpText.additionalContextHelp')}
                </p>
              </div>
            
              <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
                  onClick={() => navigate('/translator/dashboard')}
                  disabled={processing}
                >
                  {t('translationSetup.buttons.cancel')}
                </button>
                <button
                  type="button"
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleStartTranslation}
                  disabled={processing || !uploadedDocument}
                >
                  {processing ? (
                    <div className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('common.processing')}
                    </div>
                  ) : (
                    t('translationSetup.buttons.startTranslation')
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default TranslationSetup;
